import axios, { AxiosRequestConfig, ResponseType } from 'axios';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { checkConnection } from '../utils/connection';
import env from '../env';
import UseToast from '../hooks/useToast';
import { normalizeArabicNumeralsDeep } from '../utils/arabicNumerals';

let isSessionExpiredHandling = false;

axios.interceptors.request.use(
  function (config) {
    if (isSessionExpiredHandling) {
      return Promise.reject({ __sessionExpiredRedirecting: true });
    }

    if (!config.headers) {
      config.headers = {};
    }

    const token = localStorage.getItem('salon-admin-token');
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token;
    }
    config.headers['user-time-zone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;

    config.headers['Content-Type'] = 'application/json';
    config.headers['Accept'] = 'application/json';
    config.headers['env'] = env.env;


    return config;
  },

  function (error) {
    return Promise.reject(error);
  },
);

const logOutCalled = () => {
  localStorage.clear();
  window.location.replace('/signin');
};

// A 401 means the session is over: signed out elsewhere, revoked by a password change/reset,
// account deactivated or permissions changed. Clear it once and go to sign-in; never retry.
export const handleSessionEnded = (message?: string) => {
  if (isSessionExpiredHandling) {
    return;
  }

  isSessionExpiredHandling = true;
  UseToast(message || 'Session expired! Please login again.', 'error');
  logOutCalled();
};

// ── Network status ───────────────────────────────────────────────────────────
// Fixed toast ids: many parallel requests failing at once show one message, not ten.
const NETWORK_ERROR_TOAST_ID = 'network-error';
const OFFLINE_TOAST_ID = 'network-offline';
const OFFLINE_MESSAGE = "You're offline. Changes can't be saved until the connection is back.";
const RECHECK_INTERVAL_MS = 5000;

let recheckTimer: number | null = null;

const stopRechecking = () => {
  if (recheckTimer !== null) {
    window.clearInterval(recheckTimer);
    recheckTimer = null;
  }
};

// Connection confirmed: remove the banner (if it is up) and say so
const markOnline = () => {
  stopRechecking();
  if (toast.isActive(OFFLINE_TOAST_ID)) {
    toast.dismiss(OFFLINE_TOAST_ID);
    toast.success('Back online.', { position: 'top-center', autoClose: 2500 });
  }
};

const showOfflineBanner = () => {
  toast.dismiss(NETWORK_ERROR_TOAST_ID);
  toast.warning(OFFLINE_MESSAGE, {
    toastId: OFFLINE_TOAST_ID,
    position: 'top-center',
    autoClose: false,
    closeOnClick: false,
    draggable: false,
  });

  // The browser's "online" event can't be relied on to remove the banner: it never fires
  // if the browser didn't consider itself offline, and it fires before the connection
  // really works. So keep re-checking until the internet is back.
  if (recheckTimer === null) {
    recheckTimer = window.setInterval(async () => {
      if (!toast.isActive(OFFLINE_TOAST_ID)) {
        stopRechecking(); // closed by the admin
        return;
      }
      if ((await checkConnection()) === 'online') {
        markOnline();
      }
    }, RECHECK_INTERVAL_MS);
  }
};

// Any response from our server proves the connection works
const onServerResponded = () => {
  if (recheckTimer !== null || toast.isActive(OFFLINE_TOAST_ID)) {
    markOnline();
  }
};

const showRequestError = (message: string) => {
  toast.error(message, { toastId: NETWORK_ERROR_TOAST_ID, position: 'top-right' });
};

// A request that got no response at all. That alone doesn't prove the network is down:
// it can also be a timeout, a cancelled request, or our server being down/blocked (CORS).
// So the cause is worked out first. It never signs the admin out; only a real 401 does.
const handleNoResponse = async (error: unknown) => {
  if (axios.isCancel(error)) {
    return;
  }

  if (_.get(error, 'code') === 'ECONNABORTED') {
    showRequestError('The server took too long to respond. Please try again.');
    return;
  }

  if (toast.isActive(OFFLINE_TOAST_ID)) {
    return; // the offline banner already explains it
  }

  // Real connectivity check, not just navigator.onLine
  if ((await checkConnection()) === 'offline') {
    showOfflineBanner();
    return;
  }

  // The internet works, so our server is down or the request was blocked
  showRequestError('Unable to reach the server. Please try again later.');
};

// App-wide offline banner. React Query refetches active queries on reconnect by itself,
// so pages refresh once the connection is back. Returns the cleanup function.
export const watchNetworkStatus = () => {
  // "online" only means a network interface came back; confirm the internet works first.
  // If it doesn't yet, the banner's own re-check loop keeps trying.
  const onOnline = async () => {
    if (toast.isActive(OFFLINE_TOAST_ID) && (await checkConnection()) === 'online') {
      markOnline();
    }
  };

  if (!navigator.onLine) {
    showOfflineBanner();
  }

  // "offline" (navigator.onLine === false) is reliable, no check needed
  window.addEventListener('offline', showOfflineBanner);
  window.addEventListener('online', onOnline);
  return () => {
    window.removeEventListener('offline', showOfflineBanner);
    window.removeEventListener('online', onOnline);
    stopRechecking();
  };
};

const TOKEN_KEY = 'salon-admin-token';
const SESSION_ENDED_MESSAGE = 'Your session has ended. Please sign in again.';

// Tabs share localStorage: signing out (or a session ending) in one tab removes the token
// for every tab. Logged-in pages call this so the other tabs go to sign-in right away
// instead of failing on their next request. Returns the cleanup function.
export const watchSessionAcrossTabs = () => {
  const onStorage = (event: StorageEvent) => {
    // key === null means localStorage.clear(), which is what sign-out does
    const tokenRemoved = event.key === null || (event.key === TOKEN_KEY && !event.newValue);
    if (tokenRemoved && !localStorage.getItem(TOKEN_KEY)) {
      handleSessionEnded(SESSION_ENDED_MESSAGE);
    }
  };

  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
};

// Same handling for callers that use fetch() directly instead of axios
export const endSessionOnUnauthorized = async (response: Response) => {
  if (response.status !== 401 || !localStorage.getItem('salon-admin-token')) {
    return;
  }

  let message: string | undefined;
  try {
    message = _.get(await response.clone().json(), 'data.message');
  } catch (_error) {}

  handleSessionEnded(message);
};

axios.interceptors.response.use(
  function (response) {
    onServerResponded();
    // Any status code that lie within the range of 2xx cause this function to trigger
    const contentType = _.get(response, 'headers["content-type"]', '');

    if (contentType.includes('application/json')) {
      response = normalizeArabicNumeralsDeep(response.data);

      if (_.get(response, 'status') && _.get(response, 'data.success')) {
        const token = _.get(response, 'data.token');
        if (token) {
          localStorage.setItem('salon-admin-token', token);
        }

        return response;
      } else {
        const message = _.get(response, 'data.message', 'Something went wrong.');

        UseToast(message, 'error');
        return Promise.reject(response);
      }
    } else if (response.status === 200) {
      return response;
    }

    return Promise.reject(response);
  },
  function (error) {
    console.log('request.ts response error:', error);

    if (_.get(error, '__sessionExpiredRedirecting')) {
      return Promise.reject(error);
    }

    const statusCode = _.get(error, 'response.status');
    if (statusCode) {
      onServerResponded();
    }
    const hasToken = Boolean(localStorage.getItem('salon-admin-token'));
    // The request was made with a session even if another tab has cleared the token since
    const sentWithToken = Boolean(_.get(error, 'config.headers.Authorization'));

    if (statusCode === 401 && (hasToken || sentWithToken || isSessionExpiredHandling)) {
      // The server's message says why (session ended, account deactivated, permissions changed)
      handleSessionEnded(_.get(error, 'response.data.data.message'));
      return Promise.reject(error);
    }

    if (!statusCode) {
      // No response at all: find out why (offline / timeout / server unreachable)
      handleNoResponse(error);
      return Promise.reject(error);
    }

    // Any status codes that falls outside the range of 2xx cause this function to trigger
    const defaultMsg = statusCode === 404 ? 'API not found.' : 'Something went wrong.';
    const message = _.get(error, 'response.data.data.message') || _.get(error, 'response.data.message') || defaultMsg;

    UseToast(message, 'error');

    // Callers that opted in (rawError) need the status code, headers and payload, e.g. 401/429 on admin OTP
    if (statusCode === 409 || _.get(error, 'config.rawError')) return Promise.reject(error);

    return Promise.reject(message);
  },
);

const request = async ({
  url,
  method = 'GET',
  params,
  body,
  headers,
  responseType,
  customURl = false,
  rawError = false,
}: {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: any;
  body?: any;
  headers?: any;
  responseType?: ResponseType;
  customURl?: boolean;
  // Reject non-2xx responses with the axios error instead of the message string
  rawError?: boolean;
}) => {
  const BASE_URL = `${env.app.backend_url}${customURl ? '/api/v1' : env.app.api_prefix}`;
  // const BASE_URL = `${env.app.backend_url}${env.app.api_prefix}${env.app.api_prefix_email}`

  const res = await axios.request({
    url: BASE_URL + url,
    method,
    params,
    data: body,
    headers,
    responseType,
    rawError,
  } as AxiosRequestConfig);

  return res;
};
export const requestForEmail = async ({
  url,
  method = 'GET',
  params,
  body,
  headers,
  responseType,
}: {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  params?: any;
  body?: any;
  headers?: any;
  responseType?: ResponseType;
}) => {
  const BASE_URL = `${env.app.backend_url}${env.app.api_prefix_email}`;

  const res = await axios.request({
    url: BASE_URL + url,
    method,
    params,
    data: body,
    headers,
    responseType,
  });

  return res;
};

export default request;
